import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  /** Show a red border + ring; pair with an external error message. */
  error?: boolean;
  /** Whether the user can clear the current selection. */
  clearable?: boolean;
  /** `cell` drops the resting border so the control reads as a table cell until
   *  it is hovered or focused. For editable data grids; `default` everywhere else. */
  variant?: 'default' | 'cell';
  /** Render the menu into `document.body`, fixed-positioned against the trigger.
   *  Required inside anything that clips — an `overflow-x-auto` table wrapper
   *  also clips vertically, because CSS forces `overflow-y` to `auto` when the
   *  other axis is not `visible`, so an absolutely-positioned menu is cut off at
   *  the container's edge. Same escape hatch SupernovaDatePicker offers. */
  portal?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results',
  className,
  disabled,
  error,
  clearable = true,
  variant = 'default',
  portal = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  /** Fixed-position coords for the portaled menu (only used when `portal`). */
  const [menuPos, setMenuPos] = useState<{
    left: number;
    top: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      // The menu may be portaled outside containerRef, so exempt it too —
      // otherwise this mousedown unmounts it before the option's click fires
      // and the selection silently never happens.
      if (menuRef.current?.contains(target)) return;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Glue the portaled menu to the trigger, flipping above when there is not
  // room below, and capping the height to whichever side it lands on.
  const repositionMenu = useCallback(() => {
    const anchor = containerRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const gap = 4;
    const menuH = menuRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - r.bottom - gap;
    const spaceAbove = r.top - gap;
    const flipUp = menuH > 0 && spaceBelow < menuH && spaceAbove > spaceBelow;
    const room = Math.max(160, (flipUp ? spaceAbove : spaceBelow) - gap);
    const top = flipUp ? Math.max(gap, r.top - gap - Math.min(menuH, room)) : r.bottom + gap;
    const maxHeight = Math.max(140, Math.min(room, window.innerHeight - top - gap));
    setMenuPos({ left: r.left, top, width: r.width, maxHeight });
  }, []);

  useLayoutEffect(() => {
    if (!open || !portal) return;
    repositionMenu();
    // Second pass once the menu has painted: the first call measures a height
    // of 0, which is what decides the flip. `query` re-runs it because
    // filtering changes the height.
    const raf = requestAnimationFrame(repositionMenu);
    const onScrollOrResize = () => repositionMenu();
    // Capture phase so a scrolling ancestor (the table wrapper) triggers it too.
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, portal, repositionMenu, query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const selectedOption = useMemo(() => options.find((o) => o.value === value), [options, value]);

  const select = (next: string) => {
    onChange(next);
    setOpen(false);
    setQuery('');
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  const menu = (
    <div
      ref={menuRef}
      style={
        portal && menuPos
          ? {
              position: 'fixed',
              left: menuPos.left,
              top: menuPos.top,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
            }
          : undefined
      }
      className={cn(
        'z-50 flex flex-col overflow-hidden rounded-lg border border-border bg-white shadow-lg',
        !portal && 'absolute mt-1 w-full',
      )}
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
      <div className="max-h-64 min-h-0 flex-1 overflow-y-auto py-1">
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">{emptyMessage}</div>
        ) : (
          filtered.map((o) => {
            const isSelected = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => select(o.value)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted',
                  isSelected && 'bg-primary/5 text-primary',
                )}
              >
                <span className="min-w-0 flex-1 truncate">{o.label}</span>
                {isSelected && <Check className="h-4 w-4 shrink-0 text-primary" strokeWidth={3} />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (open) setQuery('');
          setOpen(!open);
        }}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 border text-left text-sm outline-none transition-colors focus:ring-1',
          variant === 'cell' ? 'rounded-md bg-transparent px-2' : 'rounded-lg bg-white px-3',
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : variant === 'cell'
              ? // Ring, not border-color: index.css's unlayered
                // `* { border-color }` beats Tailwind v4's layered utilities, so
                // `border-*` never applies here. `!` forces the resting border
                // transparent; the ring carries hover and focus.
                'border-transparent! hover:bg-white hover:ring-1 hover:ring-input focus:bg-white focus:ring-primary'
              : 'border-input hover:bg-muted/40 focus:border-primary focus:ring-primary',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', !selectedOption && 'text-muted-foreground')}>
          {selectedOption?.label ?? placeholder}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {clearable && selectedOption && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={clear}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Clear selection"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </div>
      </button>

      {open && (portal ? createPortal(menu, document.body) : menu)}
    </div>
  );
}
